import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { concat } from '@ember/helper';
import Button from 'tio-ui/components/button';
import Popover from 'tio-ui/components/popover';
import Text from 'tio-ui/components/text';
import { PhQuestion } from 'ember-phosphor-icons';
import type RouterService from '@ember/routing/router-service';

interface ContactSupportHintSignature {
  Args: {
    /** Translation namespace holding `help_label`, `help_prefix`, `help_link` and `help_suffix`. */
    copyKey: string;
    /** Names the control for automation; distinct per mounting surface. */
    testHook: string;
  };
}

/**
 * The `?` beside a resolution question's label: the member's way out when their answer is
 * not offered. It opens the contact-us page in a new tab and writes nothing — the question
 * stays pending for ops.
 */
export default class ContactSupportHint extends Component<ContactSupportHintSignature> {
  @service declare router: RouterService;

  // Resolved by the router, so it follows the member's own host and environment.
  get contactUrl() {
    return this.router.urlFor('authenticated.contact-us');
  }

  <template>
    <Popover @placement="top" @ariaLabel={{t (concat @copyKey ".help_label")}}>
      <:trigger as |popover|>
        <Button
          @appearance="minimal"
          @size="sm"
          @icon={{PhQuestion}}
          @iconSize={{16}}
          @ariaLabel={{t (concat @copyKey ".help_label")}}
          @testHook={{@testHook}}
          data-test-resolution-alert-help
          {{popover.register}}
          {{on "click" popover.toggle}}
        />
      </:trigger>
      <:body>
        <Text @size="sm" data-test-resolution-alert-help-body>
          {{t (concat @copyKey ".help_prefix")}}
          <a
            href={{this.contactUrl}}
            target="_blank"
            rel="noopener noreferrer"
            class="app-text-link app-text-link--inline"
            data-test-resolution-alert-contact-support
          >{{t (concat @copyKey ".help_link")}}</a>
          {{t (concat @copyKey ".help_suffix")}}
        </Text>
      </:body>
    </Popover>
  </template>
}
